/* =========================================================================
   MONDAIN — the shell engine. Vanilla, no build step, no dependencies.
   Owns: the glass card's scroll state, the motion nav indicator, live
   search, the search panel, the mobile menu, the cart drawer, and the
   footer status line. Every surface page loads this file with `defer`.

   Brand marks are NOT here any more: Seamas supplied the real SVGs on
   2026-08-10 and assets/logos.css paints them as masks over currentColor
   (`.mdn-mark .mdn-mark--word|--spiral|--footer|--smoking`). The old
   recovered path data + [data-mdn-mark] hydration are deleted.

   Public API (surfaces use it, never reach past it):
     MDN.cart.add({title, variant, price, ph})
     MDN.cart.open() / .close()
     MDN.on('cart:change', fn)
   ========================================================================= */

(function () {
  'use strict';

  /* ------------------------------------------------------------------
     RESTORED 2026-08-10 by the HOME session, cross-owner emergency fix.
     shell.js was throwing `ReferenceError: on is not defined` at the
     window.MDN assignment, which killed the ENTIRE shell on every page:
     no data-m-top scroll state, no cart, no footer clock, no nav behaviour.
     Seamas saw a page where nothing appeared to have changed.

     Cause: the bus was removed when mark injection was retired, but both
     `on`/`emit` remained in the public API (and `emit()` is still called
     twice in the cart path below, so the cart was a second latent throw).
     Restoring the bus rather than deleting the API, since this file's own
     header documents MDN.on(). Shell session: yours to keep or replace.
     ------------------------------------------------------------------ */
  var bus = document.createElement('span');

  function on(name, fn) { bus.addEventListener(name, fn); }

  function emit(name, detail) {
    bus.dispatchEvent(new CustomEvent(name, { detail: detail }));
  }

  /* ---------------------------------------------------------------- */
  /* 2. Scroll state — the card lifts, the corner wordmark flips ink   */
  /* ---------------------------------------------------------------- */

  function initScrollState() {
    var root = document.documentElement;
    var hero = document.querySelector('[data-mdn-hero]');
    var ticking = false;

    function measure() {
      ticking = false;
      var y = window.scrollY || window.pageYOffset || 0;
      // "top" = still over the hero (or within 8px of the page top when
      // there is no hero). Mark fades and the corner spiral key off this.
      var limit = hero ? Math.max(8, hero.offsetHeight - 120) : 8;
      root.setAttribute('data-m-top', String(y < limit));
      // "scrolled" = ANY scroll at all — the rail starts sliding into its
      // resting offset the moment panel 1 begins to move (msngg0sh).
      root.setAttribute('data-m-scrolled', String(y > 8));
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(measure);
    }

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  /* ---------------------------------------------------------------- */
  /* 3. The motion nav indicator                                       */
  /*    One element morphing between links — the vanilla stand-in for  */
  /*    @unlumen-ui/motion-navigation-menu (React-only).               */
  /* ---------------------------------------------------------------- */

  function initNavIndicator() {
    var nav = document.querySelector('[data-mdn-nav]');
    if (!nav) return;
    var indicator = nav.querySelector('.mdn-nav__indicator');
    var links = nav.querySelectorAll('.mdn-nav__link');
    if (!indicator || !links.length) return;

    var active = nav.querySelector('.mdn-nav__link[aria-current="page"]');
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    nav.setAttribute('data-m-indicator', 'live');

    function moveTo(link, animate) {
      if (!link) {
        indicator.classList.remove('is-live');
        return;
      }
      var navBox = nav.getBoundingClientRect();
      var box = link.getBoundingClientRect();
      if (!animate) indicator.style.transition = 'none';
      indicator.style.width = box.width + 'px';
      indicator.style.transform = 'translateX(' + (box.left - navBox.left) + 'px)';
      indicator.classList.add('is-live');
      if (!animate) {
        // force a reflow, then restore the transition
        void indicator.offsetWidth;
        indicator.style.transition = '';
      }
    }

    for (var i = 0; i < links.length; i++) {
      (function (link) {
        link.addEventListener('mouseenter', function () { moveTo(link, !reduce); });
        link.addEventListener('focus', function () { moveTo(link, !reduce); });
      })(links[i]);
    }

    nav.addEventListener('mouseleave', function () { moveTo(active, !reduce); });
    nav.addEventListener('focusout', function (event) {
      if (!nav.contains(event.relatedTarget)) moveTo(active, !reduce);
    });

    moveTo(active, false);
    window.addEventListener('resize', function () { moveTo(active, false); }, { passive: true });
  }

  /* ---------------------------------------------------------------- */
  /* 3b. Search — the FRAME only. RETIRED from this file (2026-08-15):  */
  /*     the text-only result list, its SEARCH_INDEX mirror and the     */
  /*     collapsible facet drawer that used to live here. Seamas asked  */
  /*     for filters that "dynamically render a page of filtered items  */
  /*     ... behind the search bar, replacing the current 'text only    */
  /*     dropdown list'" — that surface is assets/shop.js over the one  */
  /*     products table in assets/catalogue.js (which replaces the      */
  /*     index this file used to duplicate by hand). The shell still    */
  /*     owns the toggle, expansion, focus and Escape below; shop.js    */
  /*     fills the body. initSearch() remains only as a no-op guard so  */
  /*     a page missing shop.js degrades to a quiet field, not an       */
  /*     error.                                                         */
  /* ---------------------------------------------------------------- */

  function initSearch() {
    /* shop.js declares ownership before this runs (both deferred, it is
       ordered earlier in every page). Nothing to do here either way. */
    return;
  }


  /* ---------------------------------------------------------------- */
  /* 4. Search panel + mobile menu                                     */
  /* ---------------------------------------------------------------- */

  function initPanels() {
    var searchToggle = document.querySelector('[data-mdn-search-toggle]');
    var search = document.getElementById('mdn-search');
    var menuToggle = document.querySelector('[data-mdn-menu-toggle]');
    var menu = document.getElementById('mdn-menu');
    var scrim = document.getElementById('mdn-scrim');

    function setOpen(el, open) {
      if (el) el.setAttribute('data-open', open ? 'true' : 'false');
    }

    function closeMenu() {
      setOpen(menu, false);
      if (menuToggle) menuToggle.setAttribute('aria-expanded', 'false');
    }
    if (window.MDN) window.MDN._closeMenu = closeMenu;   /* the bag closes its siblings the same way */

    if (searchToggle && search) {
      searchToggle.addEventListener('click', function () {
        var open = search.getAttribute('data-open') !== 'true';
        setOpen(search, open);
        searchToggle.setAttribute('aria-expanded', String(open));
        if (open) {
          closeCart();   /* one header panel at a time */
          closeMenu();
          var input = search.querySelector('input');
          if (input) input.focus();
        }
      });
    }

    /* The menu is a header panel like SEARCH and BAG (2026-08-29) — it
       expands inside the card, so the card is one accordion: opening any
       panel closes the other two. No scrim: nothing floats over the page. */
    if (menuToggle && menu) {
      menuToggle.addEventListener('click', function () {
        var open = menu.getAttribute('data-open') !== 'true';
        setOpen(menu, open);
        menuToggle.setAttribute('aria-expanded', String(open));
        if (open) {
          closeCart();
          setOpen(search, false);
          if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');
        }
      });

      /* the row's links carry aria-current per page; mirror it onto the
         panel's twin so the per-page edit stays a single line */
      var current = document.querySelector('.mdn-nav__link[aria-current="page"]');
      if (current) {
        var twin = menu.querySelector('.mdn-menu__link[href="' + current.getAttribute('href') + '"]');
        if (twin) twin.setAttribute('aria-current', 'page');
      }
    }

    /* legacy: a page still carrying the old scrim gets it closed for good */
    if (scrim) setOpen(scrim, false);

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      setOpen(search, false);
      closeMenu();
      if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');
      closeCart();
    });
  }

  /* ---------------------------------------------------------------- */
  /* 5. The BAG — a header panel, not a drawer (Seamas, OG reference:   */
  /*    it expands under the bar exactly like SEARCH). MDN.cart.add()   */
  /*    keeps its signature; gallery quick-shop and the PDPs call it.   */
  /* ---------------------------------------------------------------- */

  var lines = [];

  function money(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  function renderCart() {
    var list = document.querySelector('[data-mdn-cart-lines]');
    var count = document.querySelectorAll('[data-mdn-cart-count]');
    var total = document.querySelector('[data-mdn-cart-total]');
    var qty = 0;
    var sum = 0;
    for (var i = 0; i < lines.length; i++) {
      qty += lines[i].qty;
      sum += lines[i].qty * lines[i].price;
    }
    for (var c = 0; c < count.length; c++) count[c].textContent = String(qty);
    if (total) total.textContent = money(sum);
    if (!list) return;
    if (!lines.length) {
      list.innerHTML = '<p class="mdn-bag__empty m-micro m-soft">Your bag is empty</p>';
      return;
    }
    var html = '';
    for (var j = 0; j < lines.length; j++) {
      var line = lines[j];
      /* OG layout: thumb | title / variant / [−][n][+] | price over × */
      html +=
        '<div class="mdn-bag__line">' +
          (line.img
            ? '<span class="mdn-bag__thumb mdn-bag__thumb--img"><img src="' + line.img + '"' +
                ' alt="" draggable="false"></span>'
            : '<span class="mdn-bag__thumb mdn-ph" data-ph="' + (line.ph || 'pr-1') + '"></span>') +
          '<span class="mdn-bag__meta">' +
            '<span class="m-micro">' + line.title + '</span>' +
            (line.variant ? '<span class="m-micro m-soft">' + line.variant + '</span>' : '') +
            '<span class="mdn-bag__stepper" role="group" aria-label="Quantity">' +
              '<button class="mdn-bag__qty-btn m-micro" type="button" data-mdn-line-qty="-1" data-mdn-line="' + j + '"' +
                ' aria-label="Decrease quantity">&minus;</button>' +
              '<span class="mdn-bag__qty m-micro">' + line.qty + '</span>' +
              '<button class="mdn-bag__qty-btn m-micro" type="button" data-mdn-line-qty="1" data-mdn-line="' + j + '"' +
                ' aria-label="Increase quantity">+</button>' +
            '</span>' +
          '</span>' +
          '<span class="mdn-bag__end">' +
            '<span class="m-micro">' + money(line.qty * line.price) + '</span>' +
            '<button class="mdn-bag__remove" type="button" data-mdn-line-remove="' + j + '"' +
              ' aria-label="Remove ' + line.title + '">&times;</button>' +
          '</span>' +
        '</div>';
    }
    list.innerHTML = html;
  }

  function addLine(line) {
    var key = line.title + '|' + (line.variant || '');
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].title + '|' + (lines[i].variant || '') === key) {
        lines[i].qty += line.qty || 1;
        renderCart();
        emit('cart:change', { lines: lines });
        openCart();
        return;
      }
    }
    lines.push({
      title: line.title,
      variant: line.variant || '',
      price: line.price || 0,
      qty: line.qty || 1,
      ph: line.ph || 'pr-1',
      img: line.img || null   /* real product imagery in the line (2026-08-27) */
    });
    renderCart();
    emit('cart:change', { lines: lines });
    openCart();
  }

  function setBagOpen(open) {
    var bag = document.getElementById('mdn-bag');
    if (bag) bag.setAttribute('data-open', open ? 'true' : 'false');
    var toggles = document.querySelectorAll('[data-mdn-cart-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].setAttribute('aria-expanded', String(!!open));
    }
    if (open) {
      /* one header panel at a time */
      var search = document.getElementById('mdn-search');
      var searchToggle = document.querySelector('[data-mdn-search-toggle]');
      if (search) search.setAttribute('data-open', 'false');
      if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');
      if (window.MDN && window.MDN._closeMenu) window.MDN._closeMenu();
    }
  }

  function openCart() { setBagOpen(true); }
  function closeCart() { setBagOpen(false); }

  function initCart() {
    var toggles = document.querySelectorAll('[data-mdn-cart-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].addEventListener('click', function () {
        var bag = document.getElementById('mdn-bag');
        setBagOpen(!(bag && bag.getAttribute('data-open') === 'true'));
      });
    }

    /* line steppers + removes, delegated so re-renders stay listener-free */
    var list = document.querySelector('[data-mdn-cart-lines]');
    if (list) {
      list.addEventListener('click', function (event) {
        var btn = event.target.closest ? event.target.closest('button') : null;
        if (!btn) return;
        if (btn.hasAttribute('data-mdn-line-remove')) {
          lines.splice(parseInt(btn.getAttribute('data-mdn-line-remove'), 10), 1);
        } else if (btn.hasAttribute('data-mdn-line-qty')) {
          var at = parseInt(btn.getAttribute('data-mdn-line'), 10);
          if (!lines[at]) return;
          lines[at].qty += parseInt(btn.getAttribute('data-mdn-line-qty'), 10);
          if (lines[at].qty < 1) lines.splice(at, 1);
        } else {
          return;
        }
        renderCart();
        emit('cart:change', { lines: lines });
      });
    }

    renderCart();
  }

  /* ---------------------------------------------------------------- */
  /* 6. Footer status line — live clock, in the USAL register          */
  /* ---------------------------------------------------------------- */

  function initClock() {
    var nodes = document.querySelectorAll('[data-mdn-clock]');
    if (!nodes.length) return;

    function tick() {
      var now = new Date();
      var h = now.getHours();
      var suffix = h >= 12 ? 'PM' : 'AM';
      var hh = h % 12;
      if (hh === 0) hh = 12;
      var text =
        String(hh).padStart(2, '0') + ':' +
        String(now.getMinutes()).padStart(2, '0') + ':' +
        String(now.getSeconds()).padStart(2, '0') + suffix;
      for (var i = 0; i < nodes.length; i++) nodes[i].textContent = text;
    }

    tick();
    window.setInterval(tick, 1000);
  }

  /* ---------------------------------------------------------------- */
  /* boot                                                              */
  /* ---------------------------------------------------------------- */

  function boot() {
    initScrollState();
    initNavIndicator();
    initSearch();
    initPanels();
    initCart();
    initClock();
  }

  window.MDN = {
    cart: { add: addLine, open: openCart, close: closeCart, lines: function () { return lines; } },
    money: money,
    on: on,
    emit: emit
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
/* =========================================================================
   MONDAIN — cross-page transitions, the timing half. Read assets/transitions.css
   first: it holds the design, the measurement that ruled out the View
   Transitions API on file://, and the four failure modes this version avoids.

   THE SHAPE OF A NAVIGATION

     click     -> is there a rise to play? is this a light/dark flip?
     rise      -> the nav travels to its docked position (HOME only)
     cover     -> the destination's ground sweeps over the old page (flip only)
     navigate  -> the screen is one solid colour, so the cut is invisible
     reveal    -> the new page continues the same sweep to uncover itself
     descend   -> the nav drops back under the wordmark (arriving at HOME only)

   Any step that does not apply is skipped, and if none apply the click is not
   intercepted at all — an ordinary link doing an ordinary load.

   WHAT CROSSES THE GAP: one sessionStorage record, written immediately before
   the navigation and consumed once by the next document. Verified available on
   file://. If it is missing or stale the arriving page simply loads normally,
   which is why every step here is written to be individually optional.
   ========================================================================= */

(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;

  var KEY = 'mdn:vt:from';
  /* a stale record must not animate a fresh load: without this, reloading a
     page minutes later would replay the arrival of the last navigation */
  var FRESH_MS = 4000;
  var DOCK = 'data-mdn-vt-dock';

  /* Which pages run the dark tokens. The destination's scheme has to be known
     BEFORE it loads — the veil is painted in the colour of a page that does not
     exist yet — and a static list is the honest way to know it in a build with
     no manifest. Mirrors data-m-scheme on each page's <body>; if a page's
     scheme changes, change it here too. The arriving side does not trust this
     list: it compares the recorded scheme against its own real one. */
  /* theme routes: /pages/design ends in 'design' (pageOf takes the last segment) */
  var DARK_PAGES = { 'design.html': true, 'design': true };

  var busy = false;

  function reduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function cssMs(name, fallback) {
    var raw = getComputedStyle(root).getPropertyValue(name).trim();
    var n = parseFloat(raw);
    if (!raw || isNaN(n)) return fallback;
    return /ms$/.test(raw) ? n : n * 1000;
  }

  function cssValue(name) {
    return getComputedStyle(root).getPropertyValue(name).trim();
  }

  function rail() { return doc.querySelector('.mdn-rail'); }
  function heroMark() { return doc.querySelector('.mdn-hero__mark'); }
  function isHero() { return !!(doc.body && doc.body.hasAttribute('data-m-hero')); }

  function scheme() {
    return doc.body && doc.body.getAttribute('data-m-scheme') === 'dark' ? 'dark' : 'light';
  }

  /* deliberately transitions.css's own ease, not the house --m-ease: read the
     note there — the house curve is ~90% done in the first third, which filmed
     as the nav leaping up through the wordmark */
  function ease() { return cssValue('--mdn-vt-ease') || 'ease-out'; }

  /* WHERE THE WORDMARK MUST BE GONE BY. The docked nav and the hero wordmark
     share a band, so the wordmark has to be clear before the rail arrives in
     it, exactly as it is on scroll (shell.css fades it over the first 130px of
     240px of travel). Rather than guess a duration, measure the fraction of the
     rail's travel at which its top edge reaches the wordmark's bottom — then
     the coupling holds at any viewport size, and both animations can share one
     easing curve so the ratio survives it.

     The fraction is used as a KEYFRAME OFFSET, which lives in eased-progress
     space, not in time: at offset f the rail has covered f of its travel
     whatever the curve is doing. That is the whole reason this is expressed as
     an offset rather than a shorter duration. */
  function markClearAt(parked, docked, mark) {
    var travel = parked - docked;
    if (!mark || travel <= 0) return 0.5;
    var bottom = mark.getBoundingClientRect().bottom;
    return Math.min(0.9, Math.max(0.2, (parked - bottom) / travel));
  }

  function pageOf(url) {
    var last = url.pathname.split('/').pop();
    return last || 'index.html';
  }

  /* Promise.all over animations, with a wall-clock backstop. An animation that
     never finishes — a page hidden mid-flight, a cancelled transition — must
     never be able to swallow a navigation. */
  function settled(anims, dur, done) {
    var once = false;
    var fire = function () { if (!once) { once = true; done(); } };
    Promise.all(anims.map(function (a) { return a.finished; })).then(fire, fire);
    window.setTimeout(fire, dur + 140);
  }

  /* ---------------------------------------------------------------- */
  /* the rail's two resting positions                                  */
  /* ---------------------------------------------------------------- */

  /* Docked is a clamp() and parked is a calc() of three more — getPropertyValue
     hands back the unresolved expressions, so measure instead of parsing.
     Toggling the hold attribute switches shell.css's scroll-driven rule off and
     states the docked end; reading a rect in between forces the layout the
     browser would otherwise batch, and nothing paints inside one task. */
  function railTopWithDock(on) {
    if (on) root.setAttribute(DOCK, '');
    else root.removeAttribute(DOCK);
    var el = rail();
    return el ? el.getBoundingClientRect().top : 0;
  }

  function riseNeeded() {
    if (!isHero() || !rail()) return false;
    var now = rail().getBoundingClientRect().top;
    var docked = railTopWithDock(true);
    root.removeAttribute(DOCK);
    return now > docked + 1;
  }

  /* ---------------------------------------------------------------- */
  /* 1. the rise (leaving HOME)                                        */
  /* ---------------------------------------------------------------- */

  function rise(then) {
    var el = rail();
    var mark = heroMark();
    var dur = cssMs('--mdn-vt-rise', 300);
    var from = el.getBoundingClientRect().top;
    var to = railTopWithDock(true);   /* the hold stays on: we are leaving */

    if (to >= from - 1) { root.removeAttribute(DOCK); then(); return; }

    var anims = [el.animate([{ top: from + 'px' }, { top: to + 'px' }],
      { duration: dur, easing: ease(), fill: 'both' })];

    /* the wordmark is clear before the rail reaches it, and then stays clear
       for the rest of the travel */
    if (mark) {
      var clear = markClearAt(from, to, mark);
      anims.push(mark.animate([
        { opacity: 1, offset: 0 },
        { opacity: 0, offset: clear },
        { opacity: 0, offset: 1 }
      ], { duration: dur, easing: ease(), fill: 'both' }));
    }
    settled(anims, dur, then);
  }

  /* ---------------------------------------------------------------- */
  /* 2. the veil                                                       */
  /* ---------------------------------------------------------------- */

  function makeVeil(colour, dir, phase, dur) {
    var el = doc.createElement('div');
    el.className = 'mdn-veil';
    el.setAttribute('data-dir', dir);
    el.setAttribute('data-phase', phase);
    el.setAttribute('aria-hidden', 'true');
    el.style.setProperty('--mdn-veil-c', colour);
    el.style.setProperty('--mdn-veil-dur', dur + 'ms');
    doc.body.appendChild(el);
    return el;
  }

  function groundFor(sch) {
    return cssValue(sch === 'dark' ? '--mdn-veil-dark' : '--mdn-veil-light') ||
      (sch === 'dark' ? '#0a0a0a' : '#ffffff');
  }

  /* ---------------------------------------------------------------- */
  /* 3. leaving: assemble the steps this navigation actually needs     */
  /* ---------------------------------------------------------------- */

  /* Written for EVERY internal navigation, including the ones this file does
     not intercept. The arriving page needs it even when the outgoing page had
     nothing to play: GALLERY -> HOME has no rise and no flip, but HOME still
     wants to arrive with its nav docked and descend into place. Filmed without
     this and HOME simply loaded parked, which was the bug. */
  function handOff(dir) {
    try {
      sessionStorage.setItem(KEY, JSON.stringify({
        from: pageOf(new URL(location.href)),
        scheme: scheme(),
        dir: dir,
        at: Date.now()
      }));
    } catch (err) { /* private mode: the arrival is skipped, the load is not */ }
  }

  function leave(href, toScheme) {
    var flip = toScheme !== scheme();
    var dir = toScheme === 'dark' ? 'down' : 'up';

    var go = function () { location.assign(href); };

    var cover = function () {
      if (!flip) { go(); return; }
      var dur = cssMs('--mdn-veil-cover', 380);
      var el = makeVeil(groundFor(toScheme), dir, 'cover', dur);
      settled(el.getAnimations(), dur, go);
    };

    if (riseNeeded()) rise(cover);
    else cover();
  }

  doc.addEventListener('click', function (e) {
    if (busy) { e.preventDefault(); return; }
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var link = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!link || (link.target && link.target !== '_self') || link.hasAttribute('download')) return;

    var url;
    try { url = new URL(link.href, location.href); } catch (err) { return; }
    if (url.origin !== location.origin) return;          /* both are file:// here */
    if (!/\.html?$/.test(url.pathname)) return;          /* mailto:, #anchors, assets */
    if (url.href.split('#')[0] === location.href.split('#')[0]) return;

    if (reduced()) return;

    var toScheme = DARK_PAGES[pageOf(url)] ? 'dark' : 'light';
    var needsRise = riseNeeded();
    var needsFlip = toScheme !== scheme();

    handOff(toScheme === 'dark' ? 'down' : 'up');

    /* nothing to play on THIS side: let the link do its ordinary thing. The
       hand-off above still travels, so the arriving page can play its own part.
       A plain load is the baseline this build is allowed to fall back to, and
       intercepting one to do nothing would only add latency. */
    if (!needsRise && !needsFlip) return;

    e.preventDefault();
    busy = true;
    leave(url.href, toScheme);
  });

  /* ---------------------------------------------------------------- */
  /* 4. arriving                                                       */
  /* ---------------------------------------------------------------- */

  function readHandoff() {
    var raw = null;
    try {
      raw = sessionStorage.getItem(KEY);
      sessionStorage.removeItem(KEY);   /* one hop only — a reload gets nothing */
    } catch (err) { return null; }
    if (!raw) return null;
    var data = null;
    try { data = JSON.parse(raw); } catch (err) { return null; }
    if (!data || typeof data.at !== 'number') return null;
    return Date.now() - data.at > FRESH_MS ? null : data;
  }

  function descend() {
    var el = rail();
    if (!el) { root.removeAttribute(DOCK); return; }

    var mark = heroMark();
    var dur = cssMs('--mdn-vt-descend', 340);
    var from = el.getBoundingClientRect().top;   /* docked, held */
    var to = railTopWithDock(false);             /* parked */
    railTopWithDock(true);                       /* hold again for the travel */

    if (to <= from + 1) { root.removeAttribute(DOCK); return; }

    var anims = [el.animate([{ top: from + 'px' }, { top: to + 'px' }],
      { duration: dur, easing: ease(), fill: 'both' })];

    /* coming down, the wordmark waits until the rail has left its band and then
       fades up into the space — the rise, in reverse, with the same coupling */
    if (mark) {
      var clear = 1 - markClearAt(to, from, mark);
      anims.push(mark.animate([
        { opacity: 0, offset: 0 },
        { opacity: 0, offset: Math.max(0.05, Math.min(0.8, clear)) },
        { opacity: 1, offset: 1 }
      ], { duration: dur, easing: ease(), fill: 'both' }));
    }

    var released = false;
    var release = function () {
      if (released) return;
      released = true;
      /* hand `top` back to the scroll timeline BEFORE cancelling, so the two
         never disagree for a frame. At scrollY 0 they hold the same value. */
      root.removeAttribute(DOCK);
      anims.forEach(function (a) { try { a.cancel(); } catch (err) {} });
      window.removeEventListener('scroll', release);
    };

    /* scrolling into the descent ends it: the scroll timeline owns the rail,
       and fighting a user's gesture is what made the old header feel glitchy */
    window.addEventListener('scroll', release, { once: true, passive: true });
    settled(anims, dur, release);
  }

  /* This runs as a deferred script, which is before the first paint — so the
     hold and the covering veil are in place for the arriving page's very first
     frame. Anything later would flash the un-veiled page for one frame. */
  function arrive() {
    var hand = readHandoff();
    if (!hand || reduced()) return;

    /* trust the recorded scheme over DARK_PAGES: this page knows its own */
    var flip = hand.scheme !== scheme();
    var revealDur = cssMs('--mdn-veil-reveal', 460);
    var held = false;

    if (isHero() && rail()) {
      root.setAttribute(DOCK, '');
      held = true;
    }

    if (flip) {
      var dir = hand.dir === 'up' || hand.dir === 'down'
        ? hand.dir
        : (scheme() === 'dark' ? 'down' : 'up');
      var el = makeVeil(groundFor(scheme()), dir, 'reveal', revealDur);
      settled(el.getAnimations(), revealDur, function () { el.remove(); });
      /* the nav starts descending while the veil is still clearing, so the two
         read as one arrival rather than a queue of effects */
      if (held) window.setTimeout(descend, Math.round(revealDur * 0.45));
    } else if (held) {
      /* a short beat first: with no veil to clear, starting on the very first
         frame reads as the page arriving mid-motion. Let it land, then move. */
      window.setTimeout(descend, 120);
    }
  }

  if (doc.body) arrive();
  else doc.addEventListener('DOMContentLoaded', arrive);
})();
/* =========================================================================
   MONDAIN — the footer lockup's stop-motion twitch.

   Seamas: "can you apply a subtle animation to the footer Mondain? I want it
   to be a stop-motion 'twitch' of the letters thats quite slow and subtle."

   THE REFERENCE (named by Seamas, v5): the animated logo gif at the end of
   the journal book — media/journal/mondain.gif. What that gif does IS the
   design: a FEW fixed drawings, each HELD, looping. Not noise.

   HOW IT GOT HERE — v1-v4, kept so the dial is never turned the wrong way
   again:

     v1  three fields re-seeded per beat + a position wobble, ~1.2s
         -> "jarring": the wobble moved the whole word
     v2  one field per beat, 400ms                   -> "speed up 500%"
     v3  80ms                                        -> "by 1000%"
     v4  8ms, a fully random field every painted frame
         -> "looks too much like ants crawling and it gives me the heebie
            jeebies... the twitches are too fluid when I actually want
            something very very subtle, almost you have to look at it
            closely for a second before you realize its moving"

   v4's failure is the instructive one: RANDOM re-seeding is a completely
   unrelated displacement field every frame, and at speed that unbroken
   stream of uncorrelated change reads as crawling. The gif's
   stillness-with-a-twitch is the opposite structure, and v5 copies it:

     - a SLOW, slightly irregular beat (~2 a second), each frame HELD;
     - per beat the SAME fibre field FLEXES: the two displacement maps fed
       by it step their scale through a small fixed pool around the resting
       2.8, so ink edges tighten and relax by well under a pixel. The change
       is CORRELATED — the drawing resettles, nothing relocates;
     - every fifth beat the paper tooth (grain) re-seeds from a fixed
       three-seed pool, cycling — a faint repeating glint, the way a short
       gif loop repeats, rather than a boil;
     - fiber and breakN are NEVER re-seeded any more. That was the ants.
     - wanderN and the dust/fringe fields stay untouched as always:
       re-seeding wander relocates whole pencil passes, and dancing dust
       pulls the eye.

   COST: two attribute writes per beat at ~2Hz, only while the lockup is on
   screen (IntersectionObserver) and the tab is visible. Reduced motion: no
   frames at all — a redraw flicker is still motion.
   ========================================================================= */

(function () {
  'use strict';

  var lockup = document.querySelector('.mdn-footer__lockup');
  var filter = document.getElementById('mdn-graphite');
  if (!lockup || !filter) return;

  /* the two maps displaced by the fibre field — the ink edge and the interior
     trace passes; one scale value drives both so the drawing moves as one */
  var maps = [].slice.call(filter.querySelectorAll('feDisplacementMap'))
    .filter(function (m) { return m.getAttribute('in2') === 'fiber'; });
  var grain = filter.querySelector('feTurbulence[result="grain"]');
  if (!maps.length) return;

  /* the four drawings of the loop, cycled IN ORDER — randomness is what read
     as crawling. Resting scale 2.8, flexed by up to ~0.55. */
  var SCALES = ['2.8', '3.35', '2.45', '3.05'];
  var GRAIN_SEEDS = ['11', '407', '213'];

  /* v6, Seamas: "Speed up the 'twitch' by 800%" — 520ms -> 65ms. v7: "Okay
     slow that by 50%" — 130ms, ~7 beats a second. The v5 STRUCTURE stays
     through every tempo change: the fixed four-frame flex loop is what keeps
     speed from turning back into ants (v4's crawl was random fields at
     speed, not speed itself). */
  var STEP_MS = 130;
  var at = 0;
  var beats = 0;

  function reduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function frame() {
    at = (at + 1) % SCALES.length;
    for (var i = 0; i < maps.length; i++) maps[i].setAttribute('scale', SCALES[at]);
    beats++;
    if (grain && beats % 5 === 0) {
      grain.setAttribute('seed', GRAIN_SEEDS[(beats / 5) % GRAIN_SEEDS.length]);
    }
  }

  var visible = false;
  var timer = 0;

  function tick() {
    timer = 0;
    if (!visible || document.hidden || reduced()) return;
    frame();
    schedule();
  }

  function schedule() {
    if (timer) return;
    /* a hand does not keep perfect time */
    timer = window.setTimeout(tick, STEP_MS + Math.floor(Math.random() * 48) - 16);
  }

  new IntersectionObserver(function (entries) {
    visible = entries[entries.length - 1].isIntersecting;
    if (visible) schedule();
  }).observe(lockup);

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden && visible) schedule();
  });
})();
/* =========================================================================
   MONDAIN — media protection. Seamas (2026-08-15): "ensure all logos /
   images on the site aren't copyable / downloadable / draggable as much as
   possible" — several of the marks are designs sold on POD product.

   WHAT THE EXPOSURE ACTUALLY IS in this build, so the measures fit it:

   - Nearly all imagery is CSS-painted: the product/editorial placeholders
     (.mdn-ph) are gradients, and every logo (.mdn-mark) is a data-URI mask.
     None of that is draggable or right-click-savable to begin with — the
     browser only offers those verbs for real <img>/<video> elements.
   - The real elements are the journal's prints (already locked down in
     journal.css: pointer-events none + no drag, with the POD marks' lower
     thirds faded IN THE SOURCE FILES) and the HOME hero film.

   The measures, layered:
     1. CSS (base.css): no drag ghost, no selection halo on any media or mark.
     2. dragstart is refused for media, marks, placeholders and product cards
        — covers Firefox, where -webkit-user-drag does not exist.
     3. contextmenu is refused ONLY when it lands on an <img>/<video> — that
        is the "Save Image/Video As" vector. It stays available everywhere
        else on the page; blanket right-click blocking punishes the reader.
     4. draggable=false is stamped on every current and future <img>/<video>
        (MutationObserver, because shop.js and feed.js render media later).

   HONESTY CLAUSE: this is a static file:// build — everything it shows, it
   ships. A determined person can read the source or the media/ directory;
   "as much as possible" here means no casual drag-out, no save-as, no
   selection copy, and (for the POD marks) no complete logo ever on screen.
   ========================================================================= */

(function () {
  'use strict';

  var GUARDED = 'img, video, .mdn-mark, .mdn-ph, .mdn-card, .mdn-shop__card, ' +
    '.mdn-corner, .mdn-hero__mark, .mdn-footer__lockup';

  document.addEventListener('dragstart', function (e) {
    if (e.target && e.target.closest && e.target.closest(GUARDED)) e.preventDefault();
  });

  document.addEventListener('contextmenu', function (e) {
    if (e.target && e.target.closest && e.target.closest('img, video')) e.preventDefault();
  });

  function stamp(root) {
    var media = root.querySelectorAll ? root.querySelectorAll('img, video') : [];
    for (var i = 0; i < media.length; i++) media[i].setAttribute('draggable', 'false');
  }

  function init() {
    stamp(document);
    new MutationObserver(function (records) {
      for (var r = 0; r < records.length; r++) {
        for (var n = 0; n < records[r].addedNodes.length; n++) {
          var node = records[r].addedNodes[n];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('img, video')) node.setAttribute('draggable', 'false');
          stamp(node);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
/* =========================================================================
   MONDAIN — smooth scroll: RETIRED 2026-08-10. Intentionally empty.

   This file briefly held a wheel-lerp engine (rAF easing window.scrollTo,
   per annotation msmoxl47 "install smoothscroll"). Seamas's later feedback
   overruled it, twice, on feel:
     - HOME: "a hesitation before it moves to the next panel" — fixed by
       deleting ALL deck scroll JS and going pure native snap.
     - GALLERY: "this entire page needs to have scrolling revisited so its
       as smooth as the MONDAIN DESIGN tab" — DESIGN is fully native; it
       was the only page this engine stood down on.
   The site standard is NATIVE scrolling everywhere: CSS scroll-snap where
   a surface wants cadence, html { scroll-behavior: smooth } for anchors,
   and no JS between the wheel and the scroll position. Do not re-add a
   scroll engine here; the <script> tags loading this file are kept so
   stale pages fail soft, and as a tombstone so the lesson isn't relearned.
   ========================================================================= */


/* =========================================================================
   SHOPIFY CART ADAPTER (parity phase 5, 2026-08-29): the gold shell's bag
   panel, backed by the REAL cart. Overrides MDN.cart's add with an
   /cart/add.js call and re-renders the panel from /cart.js — markup and
   behaviour stay the gold bag's.
   ========================================================================= */
(function () {
  'use strict';
  if (!window.MDN || !window.MDN.cart) return;

  var lines = document.querySelector('[data-mdn-cart-lines]');
  var total = document.querySelector('[data-mdn-cart-total]');
  /* the header's count span is [data-mdn-cart-count] (gold markup); the
     first cut of this adapter looked for a name that exists nowhere */
  var counts = document.querySelectorAll('[data-mdn-cart-count]');

  function money(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  /* the free-shipping line is a live meter (Seamas, 2026-08-31): the
     remaining distance to $120 USD (converted to the buyer's currency
     when Shopify presents one), green once unlocked */
  function freeship(totalCents) {
    var els = document.querySelectorAll('[data-mdn-freeship]');
    if (!els.length) return;
    var thresh = 12000, sym = '$', code = '';   /* $120 USD (Seamas, 2026-09-18; was $100) */
    try {
      if (window.Shopify && Shopify.currency && Shopify.currency.active !== 'USD' &&
          parseFloat(Shopify.currency.rate)) {
        thresh = Math.ceil(12000 * parseFloat(Shopify.currency.rate));
        var SYM = { USD: '$', AUD: '$', CAD: '$', HKD: '$', NZD: '$', SGD: '$', EUR: '\u20ac', GBP: '\u00a3', JPY: '\u00a5', KRW: '\u20a9', ILS: '\u20aa', PLN: 'z\u0142 ', CZK: 'K\u010d ', DKK: 'kr ', SEK: 'kr ', NOK: 'kr ', MYR: 'RM ', CHF: 'CHF ', AED: 'AED ' };
        sym = SYM[Shopify.currency.active] || (Shopify.currency.active + ' ');
      }
    } catch (e) {}
    var need = thresh - totalCents;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (need <= 0) {
        el.textContent = "YOU'VE UNLOCKED FREE SHIPPING";
        el.classList.add('mdn-bag__note--unlocked');
      } else {
        el.textContent = sym + (need / 100).toFixed(2).replace(/\.00$/, '') + code + ' to free shipping';
        el.classList.remove('mdn-bag__note--unlocked');
      }
    }
  }

  function render(cart) {
    if (!lines) return;
    lastCart = cart;
    for (var c = 0; c < counts.length; c++) counts[c].textContent = cart.item_count;
    if (total) total.textContent = money(cart.total_price);
    freeship(cart.total_price);
    renderUpsell(cart);
    if (!cart.items.length) {
      lines.innerHTML = '<p class="mdn-bag__empty m-micro m-soft">Your bag is empty</p>';
      return;
    }
    lines.innerHTML = cart.items.map(function (it, i) {
      /* a line tagged _pack (the Essentials 3-pack) steps in whole packs and
         cannot drop below one pack — × still removes it (Seamas, 2026-09-08) */
      var pk = (it.properties && Number(it.properties._pack)) || 1;
      if (pk < 1) pk = 1;
      var atFloor = pk > 1 && it.quantity <= pk;   /* ordinary lines keep − → 0 (remove) */
      return '<div class="mdn-bag__line">' +
        (it.image
          ? '<span class="mdn-bag__thumb mdn-bag__thumb--img"><img src="' + it.image + '&width=96" alt="" draggable="false"></span>'
          : '<span class="mdn-bag__thumb mdn-ph" data-ph="pr-1"></span>') +
        '<span class="mdn-bag__meta">' +
          '<span class="m-micro">' + it.product_title + '</span>' +
          '<span class="m-micro m-soft">' + (it.variant_title || '') + '</span>' +
          '<span class="mdn-bag__stepper" role="group" aria-label="Quantity">' +
            '<button class="mdn-bag__qty-btn m-micro" type="button" data-sq="' + Math.max(0, it.quantity - pk) + '" data-sk="' + it.key + '"' + (atFloor ? ' disabled aria-disabled="true"' : '') + '>&minus;</button>' +
            '<span class="mdn-bag__qty m-micro">' + it.quantity + '</span>' +
            '<button class="mdn-bag__qty-btn m-micro" type="button" data-sq="' + (it.quantity + pk) + '" data-sk="' + it.key + '">+</button>' +
          '</span>' +
          (pk > 1 ? '<span class="m-micro m-soft">Sold in packs of ' + pk + '</span>' : '') +
        '</span>' +
        '<span class="mdn-bag__end">' +
          '<span class="m-micro">' + money(it.final_line_price) + '</span>' +
          '<button class="mdn-bag__remove" type="button" data-sq="0" data-sk="' + it.key + '">&times;</button>' +
        '</span>' +
      '</div>';
    }).join('');
  }

  /* IN-BAG UPSELL (Seamas, 2026-09-18): the Nalgene bottle offered UNDER the
     checkout button, greyed back, while the bag has something in it, no
     bottle yet, and a colour is in stock. It wears a cart line's exact
     footprint (48px thumb | words | end column). The lead reads GET FREE
     SHIPPING under the $120 bar, ADD TO YOUR ORDER over it. Colour chips
     only SELECT (black when chosen) and swap the photograph; the white
     dotted "Add to cart" confirms through the real /cart/add.js; the
     photograph links to the bottle's page. Data: window.MDN_UPSELL, written
     by snippets/mondain-shell-header.liquid from the live product. */
  var UPSELL = window.MDN_UPSELL || null;
  var SWATCH = { Brown: '#8a6a55', 'Brown Stars': '#8a6a55', 'Brown Graffiti': '#6e5040', Smoke: '#8a6a55', Clear: '#f7f7f7', Purple: '#7b5ea7', Seafoam: '#9fd9cf' };
  var upsellPick = null, lastCart = null;
  function renderUpsell(cart) {
    var slot = document.querySelector('[data-mdn-upsell-slot]');
    if (!slot) return;
    var live = UPSELL ? UPSELL.variants.filter(function (v) { return v.available && v.img; }) : [];
    var has = false;
    if (UPSELL && cart && cart.items) for (var i = 0; i < cart.items.length; i++) if (cart.items[i].product_id === UPSELL.id) has = true;
    if (!UPSELL || !live.length || !cart || !cart.items.length || has) { slot.innerHTML = ''; slot.hidden = true; return; }
    if (!upsellPick || !upsellPick.available) upsellPick = live[0];
    slot.hidden = false;
    slot.innerHTML =
      '<div class="mdn-bag__upsell">' +
        '<p class="mdn-bag__upsell-lead m-micro">' + (cart.total_price < 12000 ? 'Get free shipping:' : 'Add to your order') + '</p>' +
        '<div class="mdn-bag__line mdn-bag__upsell-line">' +
          '<a class="mdn-bag__thumb mdn-bag__thumb--img mdn-bag__upsell-photo" href="' + UPSELL.url + '" aria-label="' + UPSELL.title + '">' +
            '<img src="' + upsellPick.img + '" alt="" draggable="false"></a>' +
          '<span class="mdn-bag__meta">' +
            '<span class="m-micro">' + UPSELL.title + '</span>' +
            '<span class="m-micro m-soft">' + money(upsellPick.price) + '</span>' +
            '<span class="mdn-bag__upsell-chips" role="radiogroup" aria-label="Color">' +
              UPSELL.variants.map(function (v) {
                var on = v === upsellPick, off = !(v.available && v.img);
                return '<button class="m-chip mdn-bag__upsell-chip" type="button" role="radio" aria-checked="' + on + '"' +
                  (off ? ' disabled aria-disabled="true" title="Sold out"' : '') +
                  ' data-mdn-upsell-pick="' + v.id + '" style="--sw:' + (SWATCH[v.name] || '#ccc') + '">' + v.name + '</button>';
              }).join('') +
            '</span>' +
          '</span>' +
          '<span class="mdn-bag__end">' +
            '<button class="mdn-bag__upsell-add m-micro" type="button" data-mdn-upsell-add>Add to cart</button>' +
          '</span>' +
        '</div>' +
      '</div>';
  }
  (function wireUpsell() {
    var slot = document.querySelector('[data-mdn-upsell-slot]');
    if (!slot) return;
    slot.addEventListener('click', function (e) {
      var pick = e.target.closest('[data-mdn-upsell-pick]');
      if (pick && UPSELL) {
        for (var u = 0; u < UPSELL.variants.length; u++) {
          if (String(UPSELL.variants[u].id) === pick.getAttribute('data-mdn-upsell-pick')) upsellPick = UPSELL.variants[u];
        }
        renderUpsell(lastCart);
        return;
      }
      var add = e.target.closest('[data-mdn-upsell-add]');
      if (add && upsellPick) {
        add.disabled = true;
        window.MDN.cart.add({ variantId: upsellPick.id, price: upsellPick.price, qty: 1 });
      }
    });
  })();

  function refresh() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(render)
      .catch(function () {});   /* a failed sync must not leave a wrong count on screen */
  }

  /* the bag's free-shipping line in the buyer's currency (Seamas,
     2026-08-31: 'Replace $100 usd with the local currency equivalent') */
  var ship = document.querySelector('[data-mdn-freeship]');
  if (ship && window.Shopify && Shopify.currency && Shopify.currency.active &&
      Shopify.currency.active !== 'USD' && parseFloat(Shopify.currency.rate)) {
    var SYM = { USD: '$', AUD: '$', CAD: '$', HKD: '$', NZD: '$', SGD: '$', EUR: '\u20ac', GBP: '\u00a3', JPY: '\u00a5', KRW: '\u20a9', ILS: '\u20aa', PLN: 'z\u0142 ', CZK: 'K\u010d ', DKK: 'kr ', SEK: 'kr ', NOK: 'kr ', MYR: 'RM ', CHF: 'CHF ', AED: 'AED ' };
    ship.textContent = 'Free shipping over ' +
      (SYM[Shopify.currency.active] || (Shopify.currency.active + ' ')) +
      Math.ceil(120 * parseFloat(Shopify.currency.rate));
  }

  var goldAdd = window.MDN.cart.add;
  window.MDN.cart.add = function (line) {
    if (line && line.variantId) {
      /* Meta pixel (guarded — fires only when the layout initialised fbq) */
      if (window.fbq) {
        try {
          fbq('track', 'AddToCart', {
            content_ids: [String(line.variantId)],
            content_type: 'product',
            value: line.price ? line.price / 100 : undefined,
            currency: 'USD'
          });
        } catch (x) {}
      }
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        /* line.properties (e.g. _pack) rides along so the drawer / cart page can
           keep a pack in whole units (2026-09-08) */
        body: JSON.stringify({ items: [{ id: line.variantId, quantity: line.qty || 1, properties: line.properties || undefined }] })
      }).then(refresh).then(function () { window.MDN.cart.open(); });
      return;
    }
    goldAdd(line);   /* lines without a variant stay client-side (stubs) */
  };

  if (lines) {
    lines.addEventListener('click', function (e) {
      var b = e.target.closest('[data-sk]');
      if (!b) return;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: b.getAttribute('data-sk'), quantity: Number(b.getAttribute('data-sq')) })
      }).then(refresh);
    });
    refresh();
  }

  /* the bag's Checkout is Shopify's checkout; the cart PAGE (mondain-cart)
     announces its own changes as `mdn:cart` so the panel never goes stale */
  var checkout = document.querySelector('.mdn-bag__checkout');
  if (checkout) {
    checkout.addEventListener('click', function () { window.location.href = '/checkout'; });
  }
  document.addEventListener('mdn:cart', function () { refresh(); });
  /* bfcache restores the page with its OLD count painted; re-sync on return
     so the header never shows a stale bag after a back/forward (Seamas,
     2026-09-05 cart-desync repro) */
  window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });
  /* and pull the live cart whenever the bag is toggled, so the drawer and the
     count can never disagree with the server when the buyer looks */
  var bagToggles = document.querySelectorAll('[data-mdn-cart-toggle]');
  for (var bt = 0; bt < bagToggles.length; bt++) {
    bagToggles[bt].addEventListener('click', function () { refresh(); });
  }
})();
